import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import Layout from "../layouts/Layout";
import { API_BASE_URL, FONT_UI, SKIN_THEMES } from "../layouts/constants";

const DEFAULT_CENTER = [20.5937, 78.9629];

function useSkin() {
  const skinId = localStorage.getItem("skinTheme") || "navy";
  return SKIN_THEMES.find((s) => s.id === skinId) || SKIN_THEMES[0];
}

function api(path) {
  const base = API_BASE_URL.replace(/\/$/, "");
  if (base.endsWith("/api") && path.startsWith("/api/")) {
    return `${base}${path.replace(/^\/api/, "")}`;
  }
  return `${base}${path}`;
}

function toNum(v) {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function fmtKm(m) {
  return (Number(m || 0) / 1000).toFixed(1);
}

function fmtKph(mps) {
  return Math.round(Number(mps || 0) * 3.6);
}

function parseUtcToIST(ts) {
  if (!ts) return null;
  if (ts instanceof Date) return ts;
  if (typeof ts !== "string") return null;
  const s = ts.trim();
  if (!s) return null;
  const utcStr = /Z$|[+-]\d{2}:?\d{2}$/.test(s) ? s : `${s}Z`;
  const d = new Date(utcStr);
  return Number.isNaN(d.getTime()) ? null : d;
}

function parseDbIst(ts) {
  if (!ts) return null;
  if (ts instanceof Date) return ts;
  if (typeof ts !== "string") return null;
  const s = ts.trim();
  if (!s) return null;
  const clean = s.replace("T", " ").replace("Z", "").split(".")[0];
  const [datePart, timePart] = clean.split(" ");
  if (!datePart || !timePart) return null;
  const [year, month, day] = datePart.split("-").map(Number);
  const [hour, minute, second = 0] = timePart.split(":").map(Number);

  if (
    !Number.isFinite(year) ||
    !Number.isFinite(month) ||
    !Number.isFinite(day) ||
    !Number.isFinite(hour) ||
    !Number.isFinite(minute)
  ) {
    return null;
  }

  const d = new Date(year, month - 1, day, hour, minute, second);
  return Number.isNaN(d.getTime()) ? null : d;
}

function formatDateObj(d) {
  if (!d) return "—";
  return new Intl.DateTimeFormat("en-IN", {
    timeZone: "Asia/Kolkata",
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
  })
    .format(d)
    .replace(",", "");
}

function formatUtcAsIST(ts) {
  return formatDateObj(parseUtcToIST(ts));
}

function humanDuration(startRaw, endRaw) {
  const startD = parseDbIst(startRaw);
  const endD = endRaw ? parseUtcToIST(endRaw) : new Date();

  if (!startD || !endD) return "—";

  const ms = endD.getTime() - startD.getTime();
  if (!Number.isFinite(ms) || ms < 0) return "—";

  const s = Math.floor(ms / 1000);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const ss = s % 60;

  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${ss}s`;
  return `${ss}s`;
}

function vehicleSvg(color = "#111827", size = 54) {
  return `
    <svg viewBox="0 0 64 32" width="${size}" height="${size}" fill="${color}" xmlns="http://www.w3.org/2000/svg">
      <rect x="2" y="10" width="30" height="12" rx="2"></rect>
      <path d="M34 10h12l6 6v6H34z"></path>
      <circle cx="14" cy="26" r="4" fill="#1f2937"></circle>
      <circle cx="42" cy="26" r="4" fill="#1f2937"></circle>
      <rect x="40" y="12" width="6" height="4" fill="#93c5fd"></rect>
    </svg>
  `;
}

function vehicleIcon(color = "#111827", size = 54) {
  return L.divIcon({
    className: "escort-user-icon",
    html: vehicleSvg(color, size),
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  });
}

function pinIcon({ color = "#2563eb", label = "●", size = 34 }) {
  return L.divIcon({
    className: "escort-pin-icon",
    html: `
      <div style="
        width:${size}px;
        height:${size}px;
        border-radius:999px;
        background:${color};
        color:white;
        display:flex;
        align-items:center;
        justify-content:center;
        font-size:18px;
        font-weight:800;
        border:3px solid white;
        box-shadow:0 3px 10px rgba(0,0,0,0.35);
      ">
        ${label}
      </div>
    `,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  });
}

function checkpointIcon(index) {
  const size = 32;
  const label = String(index + 1);

  return L.divIcon({
    className: "escort-checkpoint-icon",
    html: `
      <div style="
        width:${size}px;
        height:${size}px;
        border-radius:50%;
        background:#f97316;
        color:white;
        display:flex;
        align-items:center;
        justify-content:center;
        font-size:13px;
        font-weight:800;
        border:3px solid white;
        box-shadow:0 2px 8px rgba(0,0,0,0.30);
        font-family:system-ui,sans-serif;
      ">
        ${label}
      </div>
    `,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  });
}

const fromIcon = () => pinIcon({ color: "#16a34a", label: "F" });
const toIcon = () => pinIcon({ color: "#dc2626", label: "T" });
const stopIcon = () => pinIcon({ color: "#7c3aed", label: "S" });

function usePageMeta() {
  useEffect(() => {
    document.title = "EBD Patrol";

    const svgFavicon = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64"><defs><linearGradient id="g" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" style="stop-color:#1d4ed8"/><stop offset="100%" style="stop-color:#0ea5e9"/></linearGradient></defs><path d="M32 4L8 14v18c0 14 10.5 26 24 30 13.5-4 24-16 24-30V14L32 4z" fill="url(#g)"/><path d="M22 32l7 7 13-13" stroke="white" stroke-width="4" stroke-linecap="round" stroke-linejoin="round" fill="none"/></svg>`;
    const encoded = `data:image/svg+xml,${encodeURIComponent(svgFavicon)}`;

    document
      .querySelectorAll("link[rel*='icon']")
      .forEach((el) => el.parentNode.removeChild(el));

    const link = document.createElement("link");
    link.rel = "icon";
    link.type = "image/svg+xml";
    link.href = encoded;
    document.head.appendChild(link);

    return () => {
      document.title = "EBD Patrol";
    };
  }, []);
}

export default function EscortLiveTracking() {
  const navigate = useNavigate();
  const location = useLocation();
  const skin = useSkin();

  usePageMeta();

  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const memberId = params.get("member_id") || "";
  const runId = params.get("run_id") || "";
  const limit = params.get("limit") || "2000";

  const [dashboardData, setDashboardData] = useState({
    company_name: "",
    licensedFor: "",
    admin_active: 0,
    company_address: "",
    company_phone: "",
    admin_email: "",
    company_logo: "",
    user_map: "",
  });

  const [loading, setLoading] = useState(true);
  const [pageError, setPageError] = useState("");

  const mapRef = useRef(null);
  const mapBoxRef = useRef(null);

  const latestMarkerRef = useRef(null);
  const fromMarkerRef = useRef(null);
  const toMarkerRef = useRef(null);
  const stopMarkerRef = useRef(null);

  const segmentLayersRef = useRef([]);
  const breakMarkersRef = useRef([]);
  const checkpointMarkersRef = useRef([]);

  const latestLatLngRef = useRef(null);
  const fittedOnceRef = useRef(false);
  const pollRef = useRef(null);

  const stableFromRef = useRef(null);
  const stableToRef = useRef(null);

  const checkpointsRef = useRef([]);
  const checkpointsFetchedRef = useRef(false);

  const [followLive, setFollowLive] = useState(true);
  const followLiveRef = useRef(true);

  const [status, setStatus] = useState({
    label: "—",
    className: "bg-slate-200 text-slate-800",
  });

  const [meta, setMeta] = useState({
    title: "—",
    from: "—",
    to: "—",
    vehicle: "—",
    lastTs: "—",
    distanceKm: "0.0",
    speedKph: "0",
    duration: "—",
    pointsCount: "0",
  });

  const [checkpointCount, setCheckpointCount] = useState(0);

  const routePage = useMemo(
    () => (dashboardData.user_map === "google" ? "/route-master" : "/route-master1"),
    [dashboardData.user_map]
  );

  const apiUrl = useMemo(() => {
    const q = new URLSearchParams({
      member_id: memberId,
      limit,
      include_segments: "1",
    });

    if (runId) {
      q.set("run_id", runId);
    }

    return api(`/api/escort/live/tracking?${q.toString()}`);
  }, [memberId, runId, limit]);

  const lockStablePins = useCallback((run) => {
    if (!run) return;

    if (!stableFromRef.current) {
      const lat = toNum(run.start_lat ?? run.from_lat);
      const lng = toNum(run.start_lng ?? run.from_lng);

      if (lat !== null && lng !== null) {
        stableFromRef.current = {
          lat,
          lng,
          name: run.from_name || "From",
        };
      }
    }

    if (!stableToRef.current) {
      const lat = toNum(run.end_lat ?? run.to_lat);
      const lng = toNum(run.end_lng ?? run.to_lng);

      if (lat !== null && lng !== null) {
        stableToRef.current = {
          lat,
          lng,
          name: run.to_name || "To",
        };
      }
    }
  }, []);

  const fetchCheckpoints = useCallback(async (routeIdForCheckpoints) => {
    if (!routeIdForCheckpoints) return [];

    try {
      const token = localStorage.getItem("token") || localStorage.getItem("authToken");

      const res = await fetch(
        api(`/api/checkpoints?route_id=${encodeURIComponent(routeIdForCheckpoints)}`),
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          cache: "no-store",
        }
      );

      if (!res.ok) return [];

      const data = await res.json();
      const rows = Array.isArray(data) ? data : Array.isArray(data?.data) ? data.data : [];

      return rows.filter((cp) => {
        const lat = toNum(cp.lat ?? cp.latitude);
        const lng = toNum(cp.lng ?? cp.longitude);
        return lat !== null && lng !== null;
      });
    } catch (err) {
      console.warn("Checkpoint fetch skipped:", err.message);
      return [];
    }
  }, []);

  const drawCheckpoints = useCallback((map) => {
    checkpointMarkersRef.current.forEach((m) => map.removeLayer(m));
    checkpointMarkersRef.current = [];

    const checkpoints = checkpointsRef.current;
    if (!checkpoints || checkpoints.length === 0) return;

    checkpoints.forEach((cp, index) => {
      const lat = toNum(cp.lat ?? cp.latitude);
      const lng = toNum(cp.lng ?? cp.longitude);

      if (lat === null || lng === null) return;

      const name = cp.checkpoint_name || cp.name || `Checkpoint ${index + 1}`;
      const order = cp.checkpoint_order ?? cp.seq_no ?? index + 1;

      const marker = L.marker([lat, lng], {
        icon: checkpointIcon(index),
        zIndexOffset: 800,
      })
        .addTo(map)
        .bindTooltip(
          `<div style="font-family:system-ui,sans-serif;min-width:120px;">
            <div style="font-weight:700;font-size:13px;color:#ea580c;">
              Checkpoint ${order}
            </div>
            <div style="font-size:12px;color:#374151;margin-top:2px;">
              ${name}
            </div>
          </div>`,
          {
            direction: "top",
            permanent: false,
          }
        );

      checkpointMarkersRef.current.push(marker);
    });
  }, []);

  const fetchDashboardData = useCallback(async () => {
    const token = localStorage.getItem("token") || localStorage.getItem("authToken");
    const isLoggedIn = localStorage.getItem("isLoggedIn");

    if (!token || !isLoggedIn) {
      navigate("/");
      return;
    }

    try {
      setLoading(true);
      setPageError("");

      const res = await fetch(api("/api/dashboard/admin/dashboard"), {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!res.ok) {
        throw new Error("Failed to fetch dashboard details");
      }

      const json = await res.json();

      setDashboardData((prev) => ({
        ...prev,
        ...(json?.data?.companyDetails || {}),
      }));
    } catch (error) {
      console.error("Dashboard fetch error:", error);
      setPageError(error.message || "Failed to load company details.");
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  const clearLayers = useCallback(() => {
    const map = mapRef.current;
    if (!map) return;

    [latestMarkerRef, fromMarkerRef, toMarkerRef, stopMarkerRef].forEach((ref) => {
      if (ref.current) {
        map.removeLayer(ref.current);
        ref.current = null;
      }
    });

    segmentLayersRef.current.forEach((layer) => map.removeLayer(layer));
    segmentLayersRef.current = [];

    breakMarkersRef.current.forEach((marker) => map.removeLayer(marker));
    breakMarkersRef.current = [];
  }, []);

  const setRunStatus = useCallback((runStatus) => {
    const s = String(runStatus || "").toLowerCase();

    if (s === "active") {
      setStatus({
        label: "Active",
        className: "bg-green-100 text-green-700",
      });
    } else if (s === "stopped" || s === "abandoned" || s === "completed") {
      setStatus({
        label: s.charAt(0).toUpperCase() + s.slice(1),
        className: "bg-red-100 text-red-700",
      });
    } else if (s === "planned" || s === "paused") {
      setStatus({
        label: s.charAt(0).toUpperCase() + s.slice(1),
        className: "bg-slate-200 text-slate-800",
      });
    } else {
      setStatus({
        label: "—",
        className: "bg-slate-200 text-slate-800",
      });
    }
  }, []);

  const placeEndpointMarkers = useCallback((map, bounds) => {
    const from = stableFromRef.current;
    const to = stableToRef.current;

    if (from) {
      if (fromMarkerRef.current) {
        map.removeLayer(fromMarkerRef.current);
      }

      fromMarkerRef.current = L.marker([from.lat, from.lng], {
        icon: fromIcon(),
        zIndexOffset: 900,
      })
        .addTo(map)
        .bindTooltip(`From<br>${from.name}`, {
          direction: "top",
        });

      bounds.extend([from.lat, from.lng]);
    }

    if (to) {
      if (toMarkerRef.current) {
        map.removeLayer(toMarkerRef.current);
      }

      toMarkerRef.current = L.marker([to.lat, to.lng], {
        icon: toIcon(),
        zIndexOffset: 900,
      })
        .addTo(map)
        .bindTooltip(`To<br>${to.name}`, {
          direction: "top",
        });

      bounds.extend([to.lat, to.lng]);
    }
  }, []);

  const placeStopMarker = useCallback((map, bounds, lat, lng, label) => {
    if (lat === null || lng === null) return;

    if (stopMarkerRef.current) {
      map.removeLayer(stopMarkerRef.current);
    }

    stopMarkerRef.current = L.marker([lat, lng], {
      icon: stopIcon(),
      zIndexOffset: 1000,
    })
      .addTo(map)
      .bindTooltip(`Stopped Here<br>${label || ""}`, {
        direction: "top",
      });

    bounds.extend([lat, lng]);
  }, []);

  const drawPlannedPins = useCallback(
    (run) => {
      const map = mapRef.current;
      if (!map || !run) return;

      clearLayers();
      lockStablePins(run);

      const bounds = L.latLngBounds();

      placeEndpointMarkers(map, bounds);
      drawCheckpoints(map);

      if (bounds.isValid()) {
        map.fitBounds(bounds.pad(0.2));
        fittedOnceRef.current = true;
      } else {
        map.setView(DEFAULT_CENTER, 5);
      }
    },
    [clearLayers, lockStablePins, placeEndpointMarkers, drawCheckpoints]
  );

  const drawTrack = useCallback(
    (data, runStatus) => {
      const map = mapRef.current;
      if (!map) return;

      clearLayers();

      const payload = data || {};
      const run = payload.run || {};
      let segments = Array.isArray(payload?.segments) ? payload.segments : [];

      lockStablePins(run);

      if (
        (!segments || segments.length === 0) &&
        Array.isArray(payload?.points) &&
        payload.points.length >= 2
      ) {
        segments = [
          {
            points: payload.points,
            break_from: null,
            break_to: null,
          },
        ];
      }

      const bounds = L.latLngBounds();

      segments.forEach((segment, index) => {
        const rawPoints = Array.isArray(segment?.points) ? segment.points : [];

        const coords = rawPoints
          .map((p) => [toNum(p?.lat), toNum(p?.lng)])
          .filter(([lat, lng]) => lat !== null && lng !== null);

        if (coords.length >= 2) {
          const polyline = L.polyline(coords, {
            color: index % 2 === 0 ? "#0ea5e9" : "#60a5fa",
            weight: 5,
            opacity: 0.95,
          }).addTo(map);

          segmentLayersRef.current.push(polyline);
          coords.forEach((coord) => bounds.extend(coord));
        } else if (coords.length === 1) {
          bounds.extend(coords[0]);
        }

        const a = segment?.break_from;
        const b = segment?.break_to;

        const aLat = toNum(a?.lat);
        const aLng = toNum(a?.lng);
        const bLat = toNum(b?.lat);
        const bLng = toNum(b?.lng);

        if (aLat !== null && aLng !== null && bLat !== null && bLng !== null) {
          const gap = L.polyline(
            [
              [aLat, aLng],
              [bLat, bLng],
            ],
            {
              color: "#9ca3af",
              weight: 3,
              opacity: 0.8,
              dashArray: "6,6",
            }
          ).addTo(map);

          segmentLayersRef.current.push(gap);

          bounds.extend([aLat, aLng]);
          bounds.extend([bLat, bLng]);
        }
      });

      placeEndpointMarkers(map, bounds);
      drawCheckpoints(map);

      const latestLat = toNum(payload?.latest?.lat);
      const latestLng = toNum(payload?.latest?.lng);

      const isStopped =
        runStatus === "stopped" ||
        runStatus === "abandoned" ||
        runStatus === "completed";

      if (latestLat !== null && latestLng !== null) {
        const latestLatLng = L.latLng(latestLat, latestLng);
        latestLatLngRef.current = latestLatLng;

        if (!isStopped) {
          latestMarkerRef.current = L.marker(latestLatLng, {
            icon: vehicleIcon("#111827", 54),
            zIndexOffset: 1200,
          })
            .addTo(map)
            .bindTooltip(
              `Live<br>${formatUtcAsIST(
                payload?.latest?.ts || payload?.latest?.recorded_at
              )}`,
              {
                direction: "top",
              }
            );

          if (followLiveRef.current) {
            map.panTo(latestLatLng);
          }
        } else {
          placeStopMarker(
            map,
            bounds,
            latestLat,
            latestLng,
            formatUtcAsIST(
              payload?.latest?.ts ||
                payload?.latest?.recorded_at ||
                run?.updated_at ||
                run?.end_time
            )
          );
        }

        bounds.extend(latestLatLng);
      }

      if (bounds.isValid() && !fittedOnceRef.current) {
        map.fitBounds(bounds.pad(0.15));
        fittedOnceRef.current = true;
      } else if (!bounds.isValid()) {
        map.setView(DEFAULT_CENTER, 5);
      }
    },
    [clearLayers, lockStablePins, placeEndpointMarkers, drawCheckpoints, placeStopMarker]
  );

  const refresh = useCallback(async () => {
    if (!memberId) {
      setStatus({
        label: "member_id required",
        className: "bg-red-100 text-red-700",
      });
      return;
    }

    try {
      const token = localStorage.getItem("token") || localStorage.getItem("authToken");

      const res = await fetch(apiUrl, {
        cache: "no-store",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await res.json();

      if (!res.ok || data?.ok === false || data?.success === false) {
        throw new Error(data?.error || data?.message || "API error");
      }

      const payload = data?.data || data;
      const run = payload?.run || {};
      const latest = payload?.latest || {};
      const runStatus = String(run?.status || "").toLowerCase();

      if (!checkpointsFetchedRef.current) {
        checkpointsFetchedRef.current = true;
        const routeIdForCp = run?.route_id || runId;
        const cps = await fetchCheckpoints(routeIdForCp);
        checkpointsRef.current = cps;
        setCheckpointCount(cps.length);
      }

      const lastTsRaw = latest?.ts || latest?.recorded_at || latest?.created_at || "";
      const lastTsDisplay = lastTsRaw ? formatUtcAsIST(lastTsRaw) : "—";

      setMeta({
        title: run?.title || "—",
        from: run?.from_name || "—",
        to: run?.to_name || "—",
        vehicle: run?.vehicle || "—",
        lastTs: lastTsDisplay,
        distanceKm: fmtKm(run?.distance_m || payload?.distance_m || 0),
        speedKph: String(fmtKph(latest?.speed_mps || run?.speed_mps || 0)),
        duration: humanDuration(run?.start_time, run?.end_time),
        pointsCount: String(payload?.total_points || payload?.points?.length || 0),
      });

      setRunStatus(runStatus);

      if (runStatus === "planned" && !payload?.latest) {
        drawPlannedPins(run);
      } else {
        drawTrack(payload, runStatus);
      }
    } catch (err) {
      console.error("Live tracking error:", err);

      setStatus({
        label: err.message || "API error",
        className: "bg-red-100 text-red-700",
      });
    }
  }, [
    apiUrl,
    memberId,
    runId,
    setRunStatus,
    drawPlannedPins,
    drawTrack,
    fetchCheckpoints,
  ]);

  const fitToBounds = useCallback(() => {
    const map = mapRef.current;
    if (!map) return;

    const group = L.featureGroup([]);

    [latestMarkerRef, fromMarkerRef, toMarkerRef, stopMarkerRef].forEach((ref) => {
      if (ref.current) {
        group.addLayer(ref.current);
      }
    });

    segmentLayersRef.current.forEach((layer) => group.addLayer(layer));
    checkpointMarkersRef.current.forEach((m) => group.addLayer(m));

    const bounds = group.getBounds();

    if (bounds.isValid()) {
      map.fitBounds(bounds.pad(0.15));
    }
  }, []);

  const recenter = useCallback(() => {
    const map = mapRef.current;

    if (map && latestLatLngRef.current) {
      map.panTo(latestLatLngRef.current);
    }
  }, []);

  useEffect(() => {
    followLiveRef.current = followLive;
  }, [followLive]);

  useEffect(() => {
    if (loading) return;
    if (!mapBoxRef.current || mapRef.current) return;

    const map = L.map(mapBoxRef.current, {
      zoomControl: true,
    }).setView(DEFAULT_CENTER, 5);

    mapRef.current = map;

    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      maxZoom: 20,
      attribution: "&copy; OpenStreetMap",
    }).addTo(map);

    L.control.scale({
      imperial: false,
    }).addTo(map);

    const legend = L.control({
      position: "bottomright",
    });

    legend.onAdd = () => {
      const div = L.DomUtil.create("div", "escort-legend");

      div.innerHTML = `
        <div><span style="background:#0ea5e9"></span> Live Track</div>
        <div><span style="background:#16a34a"></span> From Fixed</div>
        <div><span style="background:#dc2626"></span> To Fixed</div>
        <div><span style="background:#f97316"></span> Checkpoint</div>
        <div><span style="background:#7c3aed"></span> Stopped Here</div>
        <div><span style="background:#111827"></span> Live Vehicle</div>
      `;

      return div;
    };

    legend.addTo(map);

    const resizeMap = () => map.invalidateSize(true);

    setTimeout(resizeMap, 100);
    setTimeout(resizeMap, 500);
    setTimeout(resizeMap, 1000);

    window.addEventListener("resize", resizeMap);

    return () => {
      window.removeEventListener("resize", resizeMap);

      if (pollRef.current) {
        clearInterval(pollRef.current);
      }

      map.remove();

      mapRef.current = null;
      latestMarkerRef.current = null;
      fromMarkerRef.current = null;
      toMarkerRef.current = null;
      stopMarkerRef.current = null;
      segmentLayersRef.current = [];
      breakMarkersRef.current = [];
      checkpointMarkersRef.current = [];
      latestLatLngRef.current = null;
      fittedOnceRef.current = false;
      stableToRef.current = null;
      stableFromRef.current = null;
    };
  }, [loading]);

  useEffect(() => {
    if (loading) return;

    fittedOnceRef.current = false;
    stableToRef.current = null;
    stableFromRef.current = null;
    checkpointsRef.current = [];
    checkpointsFetchedRef.current = false;
    setCheckpointCount(0);

    clearLayers();
    refresh();

    if (pollRef.current) {
      clearInterval(pollRef.current);
    }

    pollRef.current = setInterval(refresh, 12000);

    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
      }
    };
  }, [loading, memberId, runId, clearLayers, refresh]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100 flex flex-col items-center justify-center">
        <div className="h-10 w-10 rounded-full border-4 border-blue-100 border-t-blue-600 animate-spin mb-3" />
        <p className="text-slate-500" style={{ fontFamily: FONT_UI }}>
          Loading EBD Patrol live tracking...
        </p>
      </div>
    );
  }

  return (
    <Layout
      dashboardData={dashboardData}
      routePage={routePage}
      activePage="escort"
      activeMenu="escort"
    >
      <style>{`
        .escort-user-icon,
        .escort-pin-icon,
        .escort-checkpoint-icon {
          transform: translate(-50%, -50%);
        }

        .escort-legend {
          background: #ffffff;
          padding: 10px 12px;
          border-radius: 10px;
          box-shadow: 0 2px 12px rgba(0,0,0,0.12);
          font: 12px/1.25 system-ui, Segoe UI, Roboto, Arial, sans-serif;
        }

        .escort-legend div {
          margin: 6px 0;
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .escort-legend span {
          display: inline-block;
          width: 14px;
          height: 14px;
          border-radius: 3px;
        }

        .leaflet-container {
          width: 100% !important;
          height: 100% !important;
        }
      `}</style>

      {pageError ? (
        <div
          className="mb-5 px-4 py-3.5 rounded-xl text-sm font-medium border bg-red-50 border-red-200 text-red-700"
          style={{ fontFamily: FONT_UI }}
        >
          {pageError}
        </div>
      ) : null}

      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
        <div className="flex items-center gap-3 border-b border-slate-200 bg-white px-4 py-3 flex-wrap">
          <div className="flex items-center gap-2 mr-1">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 64 64"
              width="26"
              height="26"
              aria-hidden="true"
            >
              <defs>
                <linearGradient id="shield-grad" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" style={{ stopColor: "#1d4ed8" }} />
                  <stop offset="100%" style={{ stopColor: "#0ea5e9" }} />
                </linearGradient>
              </defs>
              <path
                d="M32 4L8 14v18c0 14 10.5 26 24 30 13.5-4 24-16 24-30V14L32 4z"
                fill="url(#shield-grad)"
              />
              <path
                d="M22 32l7 7 13-13"
                stroke="white"
                strokeWidth="4"
                strokeLinecap="round"
                strokeLinejoin="round"
                fill="none"
              />
            </svg>

            <span
              className="text-base font-extrabold tracking-tight text-slate-900"
              style={{ fontFamily: FONT_UI }}
            >
              EBD Patrol
            </span>
          </div>

          <span className="text-slate-300 text-base select-none">|</span>

          <h3 className="m-0 text-base font-bold text-slate-900">
            {dashboardData.company_name || "Escort"} — Live Tracking
          </h3>

          <div className="text-xs text-slate-500">
            Member: <b className="text-slate-800">{memberId || "—"}</b>
          </div>

          {runId ? (
            <div className="text-xs text-slate-500">
              Run: <b className="text-slate-800">#{runId}</b>
            </div>
          ) : null}

          <button
            type="button"
            onClick={() => navigate("/escort-list")}
            className="ml-auto px-4 py-2 rounded-xl border border-slate-200 bg-white text-slate-700 text-sm font-semibold hover:bg-slate-50"
            style={{ fontFamily: FONT_UI }}
          >
            Escort List
          </button>

          <div className={`rounded-full px-3 py-1 text-xs font-bold ${status.className}`}>
            {status.label}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-4 border-b border-slate-200 bg-slate-50 px-4 py-2">
          <Info label="Title" value={meta.title} />
          <Info label="From" value={meta.from} />
          <Info label="To" value={meta.to} />
          <Info label="Vehicle" value={meta.vehicle} />
          <Info label="Last update" value={meta.lastTs} mono />

          {checkpointCount > 0 ? (
            <Info label="Checkpoints" value={String(checkpointCount)} />
          ) : null}
        </div>

        <div className="flex flex-wrap items-center gap-2 border-b border-slate-200 bg-white px-4 py-2">
          <button
            type="button"
            onClick={() => setFollowLive((v) => !v)}
            className="px-4 py-2 rounded-xl text-white text-sm font-bold"
            style={{
              background: followLive ? skin.gradient : "#2563eb",
              fontFamily: FONT_UI,
            }}
          >
            Follow Live
          </button>

          <button
            type="button"
            onClick={fitToBounds}
            className="px-4 py-2 rounded-xl border border-slate-200 bg-white text-blue-600 text-sm font-bold hover:bg-slate-50"
          >
            Fit to Track
          </button>

          <button
            type="button"
            onClick={recenter}
            className="px-4 py-2 rounded-xl border border-slate-200 bg-white text-blue-600 text-sm font-bold hover:bg-slate-50"
          >
            Recenter
          </button>

          <div className="flex-1" />

          <Info label="Distance" value={`${meta.distanceKm} km`} />
          <Info label="Speed" value={`${meta.speedKph} km/h`} />
          <Info label="Duration" value={meta.duration} />
          <Info label="Points" value={meta.pointsCount} />
        </div>

        <div
          ref={mapBoxRef}
          className="h-[calc(100vh-235px)] min-h-[520px] w-full overflow-hidden bg-slate-200"
        />
      </div>
    </Layout>
  );
}

function Info({ label, value, mono = false }) {
  return (
    <div className="text-xs">
      <span className="mr-1 text-slate-500">{label}:</span>
      <b className={mono ? "font-mono text-slate-800" : "text-slate-800"}>
        {value || "—"}
      </b>
    </div>
  );
}